import { createContext, useContext, useMemo } from 'react';
import type { Doc } from 'yjs';
import { createStore, useStore } from 'zustand';

import { CustomLoadingOverlay } from 'shared/components/app/CustomLoadingOverlay/CustomLoadingOverlay';

import { YjsStoreContext } from '../../yjs/YjsProvider';
import { useStaticDocumentData } from '../StaticDocumentDataProvider/StaticDocumentDataProvider';

type ActiveDocumentContextType =
  | { isStatic: true; document: Record<string, unknown> }
  | { isStatic: false; document: Doc };

const ActiveDocumentContext = createContext<ActiveDocumentContextType | null>(null);

export function useActiveDocument() {
  const context = useContext(ActiveDocumentContext);
  if (!context) {
    throw new Error('useActiveDocument must be used within an ActiveDocumentProvider');
  }
  return context;
}

const emptyYjsStore = createStore<{ yjsDocument: Doc | null }>(() => ({ yjsDocument: null }));

export function ActiveDocumentProvider({ children }: { children: React.ReactNode }) {
  // Try to find a Static Doc
  const staticDoc = useStaticDocumentData();

  // Try to find a Yjs Doc
  const yjsStoreApi = useContext(YjsStoreContext);
  const yjsDoc = useStore(yjsStoreApi ?? emptyYjsStore, state => state.yjsDocument);

  const value = useMemo(() => {
    if (staticDoc) return { document: staticDoc, isStatic: true } as const;
    if (yjsDoc) return { document: yjsDoc, isStatic: false } as const;
    return null;
  }, [staticDoc, yjsDoc]);

  if (!value) return <CustomLoadingOverlay visible />;

  return <ActiveDocumentContext.Provider value={value}>{children}</ActiveDocumentContext.Provider>;
}
